import { useCallback } from 'react'
import { useCookies } from 'react-cookie'
import useSWR from 'swr'
import { STORAGE_PREFIX } from '../../config'
import { APIDecoratorWithBaseURI } from '../../Service/api'
import { LOCAL_EXECUTION } from '../../utils/featureFlag'
import { UseSalesReportExport, useSalesReportExport } from '../sales-report'
import { AbandonedCartExportRequest, AbandonedCartRequest, AbandonedCartResponse } from './types'

export * from './types'

interface UseAbandonedCartReport {
  report: AbandonedCartResponse | undefined
  isLoading: boolean
  isValidating: boolean
  error: unknown
  refresh: () => void
}

export const useAbandonedCartReport = (eventId: number, request: AbandonedCartRequest | null): UseAbandonedCartReport => {
  const url = `producer/analytics/abandonedCartReport?eventId=${eventId}`
  const tokenKey = STORAGE_PREFIX + 'userJWT'
  const [cookies] = useCookies([tokenKey])
  const hasToken = LOCAL_EXECUTION || Boolean(cookies[tokenKey])
  const ready = hasToken && Number.isSafeInteger(eventId) && eventId > 0 && request !== null
  const body = request === null ? null : JSON.stringify(request)

  const fetcher = useCallback(async ([target, payload]: [string, string]): Promise<AbandonedCartResponse> => {
    const response = await APIDecoratorWithBaseURI().post(target, JSON.parse(payload))
    return response.data.body as AbandonedCartResponse
  }, [])

  const { data, error, isLoading, isValidating, mutate } = useSWR(ready ? [url, body] : null, fetcher, {
    keepPreviousData: true,
    revalidateOnFocus: false,
  })

  return {
    report: data,
    isLoading: isLoading || !hasToken,
    isValidating,
    error,
    refresh: () => {
      void mutate()
    },
  }
}

export const useAbandonedCartExport = (eventId: number): UseSalesReportExport<AbandonedCartExportRequest> =>
  useSalesReportExport<AbandonedCartExportRequest>(eventId, 'abandonedCartExport')
